import React, { memo, useMemo } from 'react';
import { Button, FormControl, InputLabel, MenuItem, Select, Tooltip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { FILTER_TYPE_OPTIONS, FILTER_CLASS_OPTIONS } from '../constants/constants';
import { useStyles } from '../DiagnosticRevisionTable.style';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../../../store/index';
import { useAppliedEvaluation } from '../../../../../../helpers/hooks/useEvaluation';
import { getMissedChip } from '../../../../../../helpers/diagnostics/detectionOrigin';
import { useMissedFindings } from '../../../../../../helpers/hooks/useMissedFindings';

//Filter controls for Diagnostic Revision Table
const FilterControls = ({
  typeValue,
  classValue,
  hasActiveFilters,
  availableTypes,
  availableClasses,
  onTypeChange,
  onClassChange,
  onReset,
  hoveredClass,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const opacitiesResult = useAppliedEvaluation('opacities', null, false);
  const missedChip = useMemo(() => getMissedChip(opacitiesResult), [opacitiesResult]);
  const { showMissed } = useMissedFindings();

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '100%',
          width: '100%',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <FilterListIcon size='small' className={classes.filter_controls_icon} />

          <FormControl variant='outlined' size='small' className={classes.filter_form_control}>
            <InputLabel className={classes.filter_input_label} style={{ color: hoveredClass && 'gray' }}>
              {t('diagnose.type')}
            </InputLabel>
            <Select
              value={typeValue}
              onChange={onTypeChange}
              label={t('diagnose.type')}
              className={classes.filter_select}
              disabled={!!hoveredClass}
              size='small'
              MenuProps={{
                PaperProps: {
                  className: classes.filter_menu_paper,
                },
              }}>
              {FILTER_TYPE_OPTIONS.map((option) => {
                const isAvailable = option === 'all' || availableTypes.has(option);
                return (
                  <MenuItem
                    key={option}
                    value={option}
                    className={classes.filter_menu_item}
                    disabled={!isAvailable}>
                    {option === 'all' ? t('diagnose.all') : t(`diagnose.${option}`)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl variant='outlined' size='small' className={classes.filter_form_control}>
            <InputLabel className={classes.filter_input_label} style={{ color: hoveredClass && 'gray' }}>
              {t('diagnose.class')}
            </InputLabel>
            <Select
              value={classValue}
              onChange={onClassChange}
              label={t('diagnose.class')}
              className={classes.filter_select}
              disabled={!!hoveredClass}
              MenuProps={{
                PaperProps: {
                  className: classes.filter_menu_paper,
                },
              }}>
              {FILTER_CLASS_OPTIONS.map((option) => {
                const isAvailable = option === 'all' || availableClasses.has(option);
                return (
                  <MenuItem
                    key={option}
                    value={option}
                    className={classes.filter_menu_item}
                    disabled={!isAvailable}>
                    {option === 'all' ? t('diagnose.all') : t(`revision.${option}`)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button
              variant='contained'
              size='small'
              onClick={onReset}
              className={classes.filter_reset_button}>
              {t('diagnose.reset_filters')}
            </Button>
          )}
        </div>
        {missedChip.missedExist && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Tooltip
              title={
                <Typography className={classes.tooltip_text}>
                  {t(missedChip.missedChipLabelKeys.explanation)}
                </Typography>
              }>
              <Button
                variant={'outlined'}
                size='small'
                onClick={() => dispatch(actions.setShowMissedFindings(!showMissed))}
                className={classes.missed_button}
                style={{
                  backgroundColor: showMissed ? '#367c7c' : 'transparent',
                  border: showMissed ? '1px solid #367c7c' : '1px solid #367c7c',
                  color: showMissed ? '#fff' : '#367c7c',
                }}>
                {t('diagnose.prior_findings')}
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(FilterControls);
